/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useNavigate } from 'react-router-dom'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import {
  setSelectedDate,
  setTemplate,
  setShowTemplatePreview,
  addCampaignTemplate,
  updateCampaignTemplate,
  setTemplateError,
  fetchAllCampaignTemplates,
  fetchAllCampaigns,
  fetchAllAppointmentTypes,
} from '@store/actions'
import {
  setTemplatesListChannel,
  pulseTemplateEditorPreview,
  pulseTemplateEditorResumeEdit,
  setOverviewDateFilter,
} from '@store/reducers/marketing.reducer'
import { ChangedEventArgs } from '@syncfusion/ej2-react-calendars'
import { format } from 'date-fns'
import { getFullName } from '@utils/fullname.util'
import {
  CampaignTemplateChannel,
  CampaignTemplateType,
  ICampaignRequest,
  ICampaignTemplate,
  marketingOverviewFilter,
} from '@dataTypes/marketing.type'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'

interface IMarketingHooks {
  activeTab: string
  addCampaignModalKey: number
  calendarContainerRef: React.RefObject<HTMLDivElement>
  currentTemplate: ICampaignTemplate | null
  disableSaveTemplate: boolean
  errorReducer: {
    fetch: string | null
    add: string | null
    update: string | null
    delete: string | null
    template: string | null
    hyperlink: string | null
  }
  handleBackToTemplates: () => void
  handleChangeFilter: (newFilter: string) => void
  handleChangeTab: (tab: string) => void
  handleCreateCampaign: () => void
  handleMainButtonClick: () => void
  hasNoCampaigns: boolean
  handleNextDay: () => void
  handlePrevDay: () => void
  handlePreviewTemplate: () => void
  handleSaveTemplate: () => void
  handleSendCampaignFromSmsTemplate: () => void
  isEmailTemplateEditor: boolean
  initialCampaign: ICampaignRequest | undefined
  isLoading: boolean
  loadingReducer: {
    fetch: boolean
    add: boolean
    update: boolean
    delete: boolean
  }
  mainButtonText: string | null
  onCampaignModalHide: () => void
  onDateChange: (args: ChangedEventArgs) => void
  onSettingsButtonClick: () => void
  overviewDateFilter: marketingOverviewFilter
  range: [Date, Date]
  selectedDate: string
  showAddCampaignModal: boolean
  showCalendar: boolean
  showTemplatePreview: boolean
  isPageReady: boolean
  templateEditorCanvasPreview: boolean
  toggleCalendar: () => void
}

const MARKETING_LOADING_CONTAINER = '.marketing-management-container'
const TAB_SKELETON_DURATION_MS = 500

export const useMarketingCampaign = (): IMarketingHooks => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const templates = useAppSelector((state) => state.marketing.campaignTemplates)
  const selectedDate = useAppSelector((state) => state.marketing.selectedDate)
  const showTemplatePreview = useAppSelector(
    (state) => state.marketing.showTemplatePreview,
  )
  const templateEditorCanvasPreview = useAppSelector(
    (state) => state.marketing.templateEditorCanvasPreview,
  )
  const selectedTemplate = useAppSelector(
    (state) => state.marketing.selectedTemplate,
  )
  const templatesListChannel = useAppSelector(
    (state) => state.marketing.templatesListChannel,
  )
  const currentTemplate = useAppSelector(
    (state) => state.marketing.currentTemplate,
  )
  const overviewDateFilter = useAppSelector(
    (state) => state.marketing.overviewDateFilter,
  )
  const loadingReducer = useAppSelector((state) => state.marketing.loading)
  const errorReducer = useAppSelector((state) => state.marketing.error)

  const [isLoading, setIsLoading] = useState(false)
  const [tabSkeletonKey, setTabSkeletonKey] = useState(0)
  const [isPageReady, setIsPageReady] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [mainButtonText, setMainButtonText] = useState<string | null>(null)
  const [showAddCampaignModal, setShowAddCampaignModal] =
    useState<boolean>(false)
  const [showCalendar, setShowCalendar] = useState<boolean>(false)
  const [disableSaveTemplate, setDisableSaveTemplate] = useState<boolean>(true)
  const [initialCampaign, setInitialCampaign] = useState<
    ICampaignRequest | undefined
  >(undefined)
  const [addCampaignModalKey, setAddCampaignModalKey] = useState(0)

  const [range, setRange] = useState<[Date, Date]>([new Date(), new Date()])
  const calendarContainerRef = useRef<HTMLDivElement>(null)
  const templateSaveSessionRef = useRef<{ mode: 'add' | 'update' } | null>(null)
  const isPageReadyRef = useRef(false)

  useLayoutEffect(() => {
    showLoading({
      container: MARKETING_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  useEffect(() => {
    const today = new Date()
    const prior = new Date(today)
    prior.setMonth(prior.getMonth() - 1)
    setRange([prior, today])
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        showCalendar &&
        calendarContainerRef.current &&
        !calendarContainerRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCalendar])

  const onDateChange = (args: ChangedEventArgs): void => {
    dispatch(setSelectedDate(args.value as Date)).then(() =>
      setShowCalendar(false),
    )
  }

  const handleChangeTab = (tab: string): void => {
    const shouldShowTabSkeleton = isPageReadyRef.current && tab !== activeTab

    navigate(tab)
    setActiveTab(tab)
    if (shouldShowTabSkeleton) {
      setIsLoading(true)
      setTabSkeletonKey((key) => key + 1)
    }
    if (tab !== 'templates') {
      dispatch(setShowTemplatePreview(false))
      dispatch(setTemplate({ template: null, type: 'selected' }))
    }

    switch (tab) {
      case 'overview':
        setMainButtonText(null)
        break
      case 'campaign':
        setMainButtonText('Create campaign')
        break
      case 'templates':
        setMainButtonText('Create template')
        break
    }
  }

  const handlePrevDay = (): void => {
    const current = new Date(selectedDate)
    const prior = new Date(current)
    prior.setDate(prior.getDate() - 1)
    dispatch(setSelectedDate(prior))
  }

  const handleNextDay = (): void => {
    const current = new Date(selectedDate)
    const next = new Date(current)
    next.setDate(next.getDate() + 1)
    dispatch(setSelectedDate(next))
  }

  const generateReport = (): void => {
    const fileName = `Marketing_Report_${format(range[0], 'yyyy-MMMM-dd')}_${format(range[1], 'yyyy-MMMM-dd')}.csv`

    const replacer = (_: string, value: any): string => value ?? ''

    const rows = campaigns.flatMap((campaign) =>
      campaign.campaign_message
        .filter(
          (cm) =>
            cm.date_time_updated_at &&
            new Date(cm.date_time_updated_at) >= range[0] &&
            new Date(cm.date_time_updated_at) <= range[1],
        )
        .map((cm) => ({
          campaign_id: campaign.campaign_id as string,
          name: campaign.name as string,
          channel: campaign.channel as string,
          campaign_status: campaign.campaign_status as string,
          location: campaign.provider?.clinic?.clinic_name as string,
          recipient: getFullName(cm.contact) as string,
          message: cm.content as string,
          status: cm.status as string,
          message_type: cm.message_type as string,
          date: cm.date_time_updated_at
            ? format(new Date(cm.date_time_updated_at), 'yyyy-MM-dd')
            : '',
        })),
    )

    const headers = [
      'campaign_id',
      'name',
      'channel',
      'campaign_status',
      'location',
      'recipient',
      'message',
      'status',
      'message_type',
      'date',
    ] as (keyof (typeof rows)[number])[]

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((fieldName) => JSON.stringify(row[fieldName], replacer))
          .join(','),
      ),
    ].join('\r\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCreateCampaign = useCallback((): void => {
    setShowAddCampaignModal(true)
  }, [])

  const handleMainButtonClick = (): void => {
    switch (activeTab) {
      case 'overview':
        generateReport()
        break
      case 'campaign':
        setShowAddCampaignModal(true)
        break
      case 'templates': {
        const channel =
          templatesListChannel === 'Email'
            ? CampaignTemplateChannel.email
            : CampaignTemplateChannel.sms
        const draft: ICampaignTemplate = {
          channel,
          name: '',
          type: CampaignTemplateType.saved,
          text_content: '',
          html_content: '',
          created_by: '',
          date_time_created_at: new Date().toISOString(),
          date_time_updated_at: new Date().toISOString(),
        }
        dispatch(setTemplate({ template: draft, type: 'selected' }))
        dispatch(setShowTemplatePreview(true))
        break
      }
    }
  }

  const handleBackToTemplates = (): void => {
    const channel =
      currentTemplate?.channel ??
      selectedTemplate?.channel ??
      CampaignTemplateChannel.sms
    dispatch(
      setTemplatesListChannel(
        channel === CampaignTemplateChannel.email ? 'Email' : 'SMS',
      ),
    )
    dispatch(setShowTemplatePreview(false))
    dispatch(setTemplate({ template: null, type: 'selected' }))
  }

  const validateName = (name: string): boolean => {
    if (name === '') {
      dispatch(
        setTemplateError({
          error: 'Template name is required',
          type: 'template',
        }),
      )
      return false
    } else if (
      templates.some((template) => template.name === name) &&
      name !== selectedTemplate?.name
    ) {
      dispatch(
        setTemplateError({
          error: 'Template name already exists',
          type: 'template',
        }),
      )
      return false
    }
    return true
  }

  const validateHyperlink = (): boolean => {
    if (
      currentTemplate?.html_content &&
      /<[^>]*class\s*=\s*["'][^"']*danger[^"']*["'][^>]*>/i.test(
        currentTemplate.html_content,
      )
    ) {
      dispatch(
        setTemplateError({ error: 'Hyperlink is required', type: 'hyperlink' }),
      )
      return false
    }
    return true
  }

  const handlePreviewTemplate = (): void => {
    if (templateEditorCanvasPreview) {
      dispatch(pulseTemplateEditorResumeEdit())
    } else {
      dispatch(pulseTemplateEditorPreview())
    }
  }

  const isEmailTemplateEditor =
    currentTemplate?.channel === CampaignTemplateChannel.email

  const handleSendCampaignFromSmsTemplate = (): void => {
    if (!currentTemplate) return
    const prefill: ICampaignRequest = {
      name: '',
      channel: 'SMS',
      message_status: 'Draft',
      campaign_status: 'Inactive',
      clinic_id: '',
      created_by: '',
      updated_by: '',
      location_filter: [],
      patient_status_filter: [],
      age_range_filter: [],
      appt_status_filter: [],
      recall_type_filter: [],
      celebration_type_filter: [],
      campaign_trigger_keyword: [],
      campaign_trigger_message: [],
      campaign_recipient: [],
      campaign_message: [],
      campaign_template_id: currentTemplate.campaign_template_id,
      text_content: currentTemplate.text_content || undefined,
      html_content: currentTemplate.html_content || undefined,
    }
    setInitialCampaign(prefill)
    setAddCampaignModalKey((k) => k + 1)
    dispatch(setShowTemplatePreview(false))
    navigate('campaign')
    setActiveTab('campaign')
    setMainButtonText('Create Campaign')
    setShowAddCampaignModal(true)
  }

  const handleSaveTemplate = (): void => {
    if (currentTemplate) {
      if (currentTemplate.campaign_template_id) {
        if (!validateName(currentTemplate.name) || !validateHyperlink()) {
          return
        }
        templateSaveSessionRef.current = { mode: 'update' }
        showLoading({ container: MARKETING_LOADING_CONTAINER })
        dispatch(
          updateCampaignTemplate({
            campaign_template_id: currentTemplate.campaign_template_id,
            data: {
              ...currentTemplate,
              name: currentTemplate.name,
              channel: currentTemplate.channel,
              type: currentTemplate.type,
            },
          }),
        )
      } else {
        if (!validateName(currentTemplate.name) || !validateHyperlink()) {
          return
        }
        templateSaveSessionRef.current = { mode: 'add' }
        showLoading({ container: MARKETING_LOADING_CONTAINER })
        dispatch(addCampaignTemplate(currentTemplate))
        dispatch(setTemplate({ template: currentTemplate, type: 'selected' }))
      }
    }
  }

  const toggleCalendar = (): void => {
    setShowCalendar(!showCalendar)
  }

  // To be used once the settings button is implemented
  const onSettingsButtonClick = (): void => {}

  const onCampaignModalHide = (): void => {
    setShowAddCampaignModal(false)
    setInitialCampaign(undefined)
  }

  const handleChangeFilter = useCallback(async (newFilter: string) => {
    dispatch(setOverviewDateFilter(newFilter as marketingOverviewFilter))
  }, [])

  useEffect(() => {
    if (!isLoading) return
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, TAB_SKELETON_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isLoading, tabSkeletonKey])

  useEffect(() => {
    let _tab = 'overview'
    if (location.pathname.includes('campaign')) {
      _tab = 'campaign'
    } else if (location.pathname.includes('templates')) {
      _tab = 'templates'
    }
    handleChangeTab(_tab)
    setActiveTab(_tab)
  }, [location.pathname])

  useEffect(() => {
    if (currentTemplate) {
      const serializeEmailCanvas = (
        c?: ICampaignTemplate['email_editor_canvas'],
      ): string =>
        JSON.stringify({
          page_color: c?.page_color ?? '#ffffff',
          page_width: c?.page_width ?? '520',
          page_height: c?.page_height ?? '',
          header_band_color: c?.header_band_color ?? '',
          cta_button_bg_color: c?.cta_button_bg_color ?? '',
          cta_button_alt_bg_color: c?.cta_button_alt_bg_color ?? '',
          cta_button_text_color: c?.cta_button_text_color ?? '',
          cta_button_alt_text_color: c?.cta_button_alt_text_color ?? '',
        })
      setDisableSaveTemplate(
        currentTemplate.campaign_template_id ===
          selectedTemplate?.campaign_template_id &&
          currentTemplate.name === selectedTemplate?.name &&
          currentTemplate.channel === selectedTemplate?.channel &&
          currentTemplate.html_content === selectedTemplate?.html_content &&
          currentTemplate.text_content === selectedTemplate?.text_content &&
          currentTemplate.url === selectedTemplate?.url &&
          currentTemplate.type === selectedTemplate?.type &&
          serializeEmailCanvas(currentTemplate.email_editor_canvas) ===
            serializeEmailCanvas(selectedTemplate?.email_editor_canvas),
      )
    } else {
      setDisableSaveTemplate(true)
    }
  }, [currentTemplate, selectedTemplate])

  useEffect(() => {
    let cancelled = false
    const startedAt = Date.now()

    showLoading({
      container: MARKETING_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
    void Promise.all([
      dispatch(fetchAllCampaigns()),
      dispatch(fetchAllCampaignTemplates()),
      dispatch(fetchAllAppointmentTypes()),
    ])
      .then(async () => {
        await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
      })
      .finally(() => {
        if (cancelled) return
        isPageReadyRef.current = true
        setIsPageReady(true)
        hideLoading(MARKETING_LOADING_CONTAINER)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, showLoading, hideLoading])

  useEffect(() => {
    return () => {
      templateSaveSessionRef.current = null
      hideLoading(MARKETING_LOADING_CONTAINER)
    }
  }, [hideLoading])

  useLayoutEffect(() => {
    if (templateSaveSessionRef.current === null) {
      return
    }

    const stillLoading = loadingReducer.add || loadingReducer.update
    if (stillLoading) return

    const hasError = !!(errorReducer.add || errorReducer.update)
    if (hasError) {
      const mode = templateSaveSessionRef.current.mode
      templateSaveSessionRef.current = null
      const detail = (errorReducer.update || errorReducer.add) as string
      const fallback =
        mode === 'update'
          ? 'Failed to update campaign template.'
          : 'Failed to create campaign template.'
      showToast(createFailedToast('Error', detail?.trim() ? detail : fallback))
      hideLoading(MARKETING_LOADING_CONTAINER)
      return
    }

    const mode = templateSaveSessionRef.current.mode
    templateSaveSessionRef.current = null

    showToast(
      createSuccessToast(
        'Success',
        mode === 'update'
          ? 'Campaign template updated successfully.'
          : 'Campaign template created successfully.',
      ),
    )
    hideLoading(MARKETING_LOADING_CONTAINER)
    dispatch(setTemplate({ template: null, type: 'selected' }))
  }, [
    loadingReducer.add,
    loadingReducer.update,
    errorReducer.add,
    errorReducer.update,
    dispatch,
    showToast,
    hideLoading,
  ])

  return {
    activeTab,
    calendarContainerRef,
    currentTemplate,
    disableSaveTemplate,
    errorReducer,
    handleBackToTemplates,
    handleChangeFilter,
    handleChangeTab,
    handleCreateCampaign,
    handleMainButtonClick,
    hasNoCampaigns: campaigns.length === 0,
    handleNextDay,
    handlePrevDay,
    handlePreviewTemplate,
    handleSaveTemplate,
    handleSendCampaignFromSmsTemplate,
    isEmailTemplateEditor,
    initialCampaign,
    addCampaignModalKey,
    isLoading,
    isPageReady,
    loadingReducer,
    mainButtonText,
    onCampaignModalHide,
    onDateChange,
    onSettingsButtonClick,
    overviewDateFilter,
    range,
    selectedDate,
    showAddCampaignModal,
    showCalendar,
    showTemplatePreview,
    templateEditorCanvasPreview,
    toggleCalendar,
  }
}
